import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isTestAccount } from "@/lib/testAccounts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Instagram, CheckCircle, Ban, Clock, ExternalLink, User } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface PendingPost {
  id: string;
  user_id: string;
  post_url: string;
  status: string;
  created_at: string;
  criteria_status: string | null;
  criteria_notes: string | null;
  full_name: string | null;
  email: string | null;
}

const InstagramApprovals = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [posts, setPosts] = useState<PendingPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    const { data: postsData } = await supabase
      .from("instagram_posts")
      .select("*")
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    const filtered = (postsData || []).filter((p: any) => !isTestAccount(p.user_id));

    if (filtered.length === 0) {
      setPosts([]);
      setLoading(false);
      return;
    }

    const userIds = Array.from(new Set(filtered.map((p: any) => p.user_id)));
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("user_id, full_name, email")
      .in("user_id", userIds);

    const profileMap = new Map((profilesData || []).map((p: any) => [p.user_id, p]));

    const merged = filtered.map((p: any) => ({
      ...p,
      full_name: profileMap.get(p.user_id)?.full_name || null,
      email: profileMap.get(p.user_id)?.email || null,
    }));

    setPosts(merged as PendingPost[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchPosts();
    const channel = supabase
      .channel("admin-ig-approvals")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "instagram_posts" },
        fetchPosts
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateCriteria = async (postId: string, val: string) => {
    await supabase.from("instagram_posts").update({ criteria_status: val } as any).eq("id", postId);
    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, criteria_status: val } : p))
    );
  };

  const approvePost = async (post: PendingPost) => {
    await supabase.from("instagram_posts").update({ status: "approved" }).eq("id", post.id);

    // Generate coupon for this approved post (mirrors ClientDetail logic)
    const { data: existingCoupon } = await supabase
      .from("coupons")
      .select("id")
      .eq("user_id", post.user_id)
      .eq("reason", "instagram_share")
      .eq("booking_id", post.id)
      .maybeSingle();

    if (!existingCoupon) {
      const code = "IG-" + Math.random().toString(36).substring(2, 8).toUpperCase();
      const igExpiry = new Date();
      igExpiry.setMonth(igExpiry.getMonth() + 6);
      await supabase.from("coupons").insert({
        user_id: post.user_id,
        code,
        discount_amount: 5,
        reason: "instagram_share",
        booking_id: null,
        expires_at: igExpiry.toISOString(),
      });
      if (post.email) {
        supabase.functions
          .invoke("send-credit-email", {
            body: {
              email: post.email,
              client_name: post.full_name,
              coupon_code: code,
              discount_amount: 5,
              expires_at: igExpiry.toISOString(),
              reason: "instagram_share",
            },
          })
          .catch(() => {});
      }
    }

    toast({
      title: "Approved",
      description: `IG post approved and $5 credit issued to ${post.full_name || "client"}.`,
    });
    setPosts((prev) => prev.filter((p) => p.id !== post.id));
  };

  const rejectPost = async (post: PendingPost) => {
    await supabase.from("instagram_posts").update({ status: "rejected" }).eq("id", post.id);
    toast({ title: "Rejected", description: "IG post rejected." });
    setPosts((prev) => prev.filter((p) => p.id !== post.id));
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light tracking-[0.2em] uppercase flex items-center gap-2">
            <Instagram size={20} /> IG Credit Approvals
          </h1>
          <p className="text-xs text-muted-foreground mt-1 tracking-wide">
            Review pending Instagram posts and issue $5 credits.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/admin")}>
          Back to Dashboard
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Clock size={14} />
            Pending Review ({posts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No Instagram posts awaiting review. 🎉
            </p>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="p-4 border border-yellow-500/30 bg-yellow-500/5 rounded-lg space-y-3"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <button
                      onClick={() => navigate(`/admin/clients/${post.user_id}`)}
                      className="flex items-center gap-2 text-sm font-medium hover:underline"
                    >
                      <User size={14} />
                      {post.full_name || post.email || "Unknown client"}
                    </button>
                    <span className="text-[10px] text-muted-foreground">
                      {format(parseISO(post.created_at), "MMM d, yyyy · h:mm a")}
                    </span>
                  </div>

                  <a
                    href={post.post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary underline break-all flex items-center gap-1.5"
                  >
                    <ExternalLink size={12} />
                    {post.post_url}
                  </a>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant={
                        post.criteria_status === "meets_criteria"
                          ? "default"
                          : post.criteria_status === "unchecked" || !post.criteria_status
                            ? "secondary"
                            : "destructive"
                      }
                      className="text-[9px]"
                    >
                      {post.criteria_status === "meets_criteria"
                        ? "✓ Meets Criteria"
                        : post.criteria_status === "missing_tag"
                          ? "Missing @tag"
                          : post.criteria_status === "missing_hashtag"
                            ? "Missing hashtag"
                            : post.criteria_status === "missing_both"
                              ? "Missing tag & hashtag"
                              : "Unchecked"}
                    </Badge>
                    {post.criteria_notes && (
                      <span className="text-[10px] text-muted-foreground italic">
                        {post.criteria_notes}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Select
                      value={post.criteria_status || "unchecked"}
                      onValueChange={(val) => updateCriteria(post.id, val)}
                    >
                      <SelectTrigger className="h-7 text-[10px] w-[150px] px-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unchecked">Unchecked</SelectItem>
                        <SelectItem value="meets_criteria">Meets Criteria</SelectItem>
                        <SelectItem value="missing_tag">Missing @tag</SelectItem>
                        <SelectItem value="missing_hashtag">Missing Hashtag</SelectItem>
                        <SelectItem value="missing_both">Missing Both</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[11px] gap-1 h-7 px-3 border-green-500/30 text-green-700 hover:bg-green-500/10"
                      onClick={() => approvePost(post)}
                    >
                      <CheckCircle size={12} /> Approve & Issue Credit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[11px] gap-1 h-7 px-3 border-destructive/30 text-destructive hover:bg-destructive/10"
                      onClick={() => rejectPost(post)}
                    >
                      <Ban size={12} /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InstagramApprovals;
